import * as React from 'react';

import {
  IconRegularChevronLeft,
  IconRegularChevronRight,
} from '@notino/react-styleguide';

import { isActivationKey } from '@helpers/accessibility/isActivationKey';
import { IImageProperties } from '@helpers/utils';
import { useArrows } from '@hooks/useArrows';
import { Fade, Arrow } from '@hooks/useArrows/styled';

import { useProductDetailContext } from '../../../ProductDetailContext';

import { Image } from './Image';
import {
  ImageWrapper,
  ScrollContent,
  ScrollWrapper,
  Wrapper,
} from './ScrollThumbs.styles';
import { useDelayedHover } from './useDelayedHover';
import { useScrollSelectedIntoView } from './useScrollSelectedIntoView';

interface IProps {
  imageIndex: number;
  handleImageChange: (index: number) => void;
  handleImageClick?: (index: number) => React.MouseEventHandler<HTMLElement>;
  imagesAttrs: IImageProperties[];
  dataTestId?: string;
  thumbSize?: string;
  thumbGap?: string;
  scrollPadding?: string;
  isCentered?: boolean;
  hideOnMobile?: boolean;
  hideArrows?: boolean;
}

export const ScrollThumbs = ({
  imageIndex,
  handleImageChange,
  handleImageClick,
  imagesAttrs,
  dataTestId,
  thumbSize,
  thumbGap,
  scrollPadding,
  isCentered,
  hideOnMobile,
  hideArrows,
}: IProps) => {
  const { currentVariant } = useProductDetailContext();
  const { containerRef, scrollTo, showLeftArrow, showRightArrow } = useArrows([
    currentVariant.webId,
  ]);

  const { setItemRef } = useScrollSelectedIntoView(imageIndex, containerRef);
  const { handleMouseOver, handleMouseOut } =
    useDelayedHover(handleImageChange);

  const handleArrowKeyDown = React.useCallback(
    (direction: 'left' | 'right') => (e: React.KeyboardEvent<HTMLElement>) => {
      if (!isActivationKey(e)) {
        return;
      }

      e.preventDefault();
      scrollTo(direction);
    },
    [scrollTo]
  );

  return (
    <Wrapper data-testid={dataTestId} hideOnMobile={hideOnMobile}>
      {!hideArrows && showLeftArrow && (
        <>
          <Arrow
            position="left"
            onClick={() => scrollTo('left')}
            onKeyDown={handleArrowKeyDown('left')}
            role="button"
            tabIndex={0}
            aria-label="Scroll thumbnails left"
          >
            <IconRegularChevronLeft
              color="icon.primary"
              height="1.25rem"
              width="1.25rem"
            />
          </Arrow>
          <Fade position="left" />
        </>
      )}

      <ScrollWrapper ref={containerRef}>
        <ScrollContent
          gap={thumbGap}
          isCentered={isCentered}
          scrollPadding={scrollPadding}
        >
          {imagesAttrs.map((imageAttr, index) => (
            <ImageWrapper key={imageAttr.src} ref={setItemRef(index)}>
              <Image
                {...imageAttr}
                isSelected={index === imageIndex}
                thumbSize={thumbSize}
                onClick={handleImageClick?.(index)}
                onMouseOver={() => handleMouseOver(index)}
                onMouseOut={handleMouseOut}
              />
            </ImageWrapper>
          ))}
        </ScrollContent>
      </ScrollWrapper>

      {!hideArrows && showRightArrow && (
        <>
          <Arrow
            position="right"
            onClick={() => scrollTo('right')}
            onKeyDown={handleArrowKeyDown('right')}
            role="button"
            tabIndex={0}
            aria-label="Scroll thumbnails right"
          >
            <IconRegularChevronRight
              color="icon.primary"
              height="1.25rem"
              width="1.25rem"
            />
          </Arrow>
          <Fade position="right" />
        </>
      )}
    </Wrapper>
  );
};
